import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import AdvForm from '../../CommonComponents/AdvForm';
import Footer from '../Footer/FooterContainer';
import PlanImpact from '../PlanImpact/PlanImpactContainer';
import Loading from '../Loading/LoadingView';
import { isNumeric, isUuid, createCustomPropType } from '../../../js/app/react/validators';
import ValidationErrors from '../ValidationErrors/ValidationErrorsContainer';
import ReactHighcharts from '../../CommonComponents/Highcharts';
import DetailedAssetAllocation from '../DetailedAssetAllocation/DetailedAssetAllocationContainer';
import { Select } from '../../CommonComponents/OPL/Select/Select/Select';
import { buildGroupedSelectOptions } from '../../MultiWorkflows/SimpleSelect/SimpleSelectView';

/*
  Check if the selected model portfolio is in the list of options.
  If not, it means it was deleted, then default to the first option
*/
const simpleSelectDefaultOption = (options, value) => {
  const selection = options.find(option => option.value === value);
  if (selection) {
    return selection;
  }

  if (!selection && options.length > 0) {
    return options[0];
  }

  return {
    label: '',
    value: '',
  };
};

class AssetAllocationView extends Component {
  componentDidMount() {
    const {
      fetchData,
      clientId,
      fpId,
      goalId,
      fetchInitialPlanImpact,
      whatIf,
      whatIfTemplate,
    } = this.props;
    fetchData(clientId, fpId, goalId, whatIfTemplate.WhatIfType);
    fetchInitialPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
  }

  componentDidUpdate(previousProps) {
    const { clientId, fpId, goalId, whatIf, fetchPlanImpact, whatIfTemplate } = this.props;

    const impactTriggerProps = ['whatIf', 'fpId', 'isOPLMonteCarlo'];

    if (!isEqual(pick(this.props, impactTriggerProps), pick(previousProps, impactTriggerProps))) {
      fetchPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
    }
  }

  render() {
    const {
      apiPortfolioIdKey,
      apiPortfolioNameKey,
      applyWhatIf,
      areOptionsLoaded,
      chartData,
      clientId,
      detailedAssetAllocationClicked,
      fpId,
      goalId,
      groupTitle,
      legend,
      loading,
      modelPortfolioGroups,
      modelPortfoliosOptions,
      planImpactItems,
      storeData,
      title,
      whatIf,
      whatIfTemplate,
      portfolioId,
    } = this.props;

    const modelPortfolioSelectOptions = buildGroupedSelectOptions(
      modelPortfoliosOptions,
      modelPortfolioGroups,
    );

    const { ADV } = window;
    return (
      <div className="what-ifs-layout">
        {loading && <Loading />}
        <div className="what-ifs-section what-ifs-layout-header">
          <h3>What-If...</h3>
        </div>
        <div className="what-ifs-layout-content">
          <h4 className="what-if-group-title">{groupTitle}</h4>
          <AdvForm>
            <ValidationErrors clientId={clientId} />
            <ul className={`what-ifs-form-fields what-ifs-box${areOptionsLoaded ? ' loaded' : ''}`}>
              <li className="what-ifs-form-field">
                <h5 className="what-if-title">{title}</h5>
              </li>
              <li className="what-ifs-form-field">
                <Select
                  isPortal
                  onChange={({ label, value }) => {
                    storeData(
                      goalId,
                      whatIfTemplate.WhatIfType,
                      {
                        WhatIf: {
                          [apiPortfolioNameKey]: label,
                          [apiPortfolioIdKey]: value,
                        },
                      },
                      clientId,
                    );
                  }}
                  value={simpleSelectDefaultOption(modelPortfoliosOptions, portfolioId)}
                  options={modelPortfolioSelectOptions}
                  aria-label="Asset Allocation"
                />
              </li>
              <li className="what-ifs-form-field what-ifs-aa-layout-chart-legend">
                <div className="what-ifs-aa-layout-chart">
                  <ReactHighcharts
                    autoResize
                    container="AssetsAllocationContainer"
                    options={chartData}
                  />
                </div>
                <div className="what-ifs-aa-layout-legend">
                  <ul className="what-ifs-aa-legend-ul">
                    {legend.map(item => (
                      <li
                        key={`${item.value}-${item.label}-${item.color}`}
                        className="what-ifs-aa-legend-li"
                      >
                        <i
                          className="what-ifs-aa-legend-color"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="what-ifs-aa-legend-percent">
                          {ADV.util.number_format(item.value * 100, 1)}%
                        </span>
                        <span className="what-ifs-aa-legend-label">{item.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
              <li className="what-ifs-form-field what-ifs-aa-layout-chart-legend">
                <div className="what-ifs-aa-layout-chart" />
                <div className="what-ifs-aa-layout-legend">
                  <button
                    type="button"
                    className="what-ifs-aa-detailed-breakdown"
                    onClick={detailedAssetAllocationClicked(clientId)}
                  >
                    Detailed Breakdown
                  </button>
                </div>
              </li>
            </ul>
          </AdvForm>
        </div>
        <PlanImpact clientId={clientId} planImpactItems={planImpactItems} />
        <Footer
          clientId={clientId}
          applyToPlanCallback={applyWhatIf(clientId, fpId, goalId, whatIf, whatIfTemplate)}
        />
        <DetailedAssetAllocation
          clientId={clientId}
          name={simpleSelectDefaultOption(modelPortfoliosOptions, portfolioId).label}
          id={simpleSelectDefaultOption(modelPortfoliosOptions, portfolioId).value}
        />
      </div>
    );
  }
}
AssetAllocationView.defaultProps = {
  title: 'Untitled What If',
  groupTitle: 'Untitled What If Group',
};

AssetAllocationView.propTypes = {
  goalId: createCustomPropType(isUuid).isRequired,
  fpId: createCustomPropType(isUuid).isRequired,
  clientId: createCustomPropType(isNumeric).isRequired,
  title: PropTypes.string,
  groupTitle: PropTypes.string,
  areOptionsLoaded: PropTypes.bool.isRequired,
  whatIf: PropTypes.shape({}).isRequired,
  whatIfTemplate: PropTypes.shape({
    WhatIfType: PropTypes.string,
  }).isRequired,
  storeData: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  detailedAssetAllocationClicked: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fetchPlanImpact: PropTypes.func.isRequired,
  fetchInitialPlanImpact: PropTypes.func.isRequired,
  planImpactItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      current: PropTypes.string.isRequired,
      proposed: PropTypes.string.isRequired,
    }),
  ).isRequired,
  applyWhatIf: PropTypes.func.isRequired,
  chartData: PropTypes.shape({}).isRequired,
  modelPortfoliosOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  legend: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  apiPortfolioNameKey: PropTypes.string.isRequired,
  apiPortfolioIdKey: PropTypes.string.isRequired,
  portfolioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  modelPortfolioGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default AssetAllocationView;
